import { computed, ref, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import {
  PAGINATION_MAX_SIZE,
  type EmployeeBookItem,
  type SpiritualIndexResponse,
} from '@kitobxonlik/contracts';
import { booksApi, examsApi } from '@/api';
import dayjs from 'dayjs';

/**
 * Xodim kutubxonasi keshi. Eski API'da "bitta kitob (xodim holati bilan)"
 * endpoint'i yo'q — holat faqat ro'yxatda keladi, shuning uchun to'liq ro'yxat
 * (500 talik sahifalar) bir marta yuklanib, kitob sahifasi undan oladi.
 */
export const useLibraryStore = defineStore('library', () => {
  const books = shallowRef<EmployeeBookItem[]>([]);
  const index = shallowRef<SpiritualIndexResponse | null>(null);
  const nextEvent = shallowRef<{
    eventId: number;
    bookId: number;
    time: string;
    title: string;
    event: string;
  } | null>(null);
  const loaded = ref(false);
  const loading = ref(false);
  let pending: Promise<void> | null = null;

  /**
   * Yuklanayotgan paytdagi chaqiruv o'sha so'rovni KUTADI — avval darhol
   * qaytardi va sahifa (masalan AppShell yuklashi tugamasdan ochilgan o'qish
   * oynasi) kitobni keshda topolmay qolardi.
   */
  function loadAll(force = false): Promise<void> {
    if (pending) return pending;
    if (loaded.value && !force) return Promise.resolve();
    pending = (async () => {
      loading.value = true;
      try {
        const all: EmployeeBookItem[] = [];
        for (let page = 1; page <= 20; page++) {
          const res = await booksApi.list({ page, size: PAGINATION_MAX_SIZE });
          all.push(...res.content);
          // DIQQAT: xodim ro'yxatida numberOfElements = UMUMIY son (eski Go), shuning uchun content bo'yicha
          if (res.content.length < PAGINATION_MAX_SIZE) break;
        }
        books.value = all;
        loaded.value = true;
      } finally {
        loading.value = false;
        pending = null;
      }
    })();
    return pending;
  }

  /** Kitob qo'shildi/o'zgardi/o'chdi (boshqaruvda) — keyingi `loadAll` ro'yxatni qayta oladi */
  function invalidate(): void {
    loaded.value = false;
  }

  async function loadIndex(): Promise<void> {
    try {
      index.value = await booksApi.mySpiritualIndex();
    } catch {
      index.value = null;
    }
  }

  /** Eng yaqin kelajakdagi rejalashtirilgan imtihon (hero uchun) */
  async function loadNextEvent(): Promise<void> {
    try {
      const list = await examsApi.myEvents({
        page: 1,
        size: 100,
        time_from: dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      });
      const upcoming = list
        .filter((e) => dayjs(e.event.time).isAfter(dayjs().subtract(1, 'hour')))
        .sort((a, b) => a.event.time.localeCompare(b.event.time))[0];
      nextEvent.value = upcoming
        ? {
            eventId: upcoming.event.id,
            bookId: upcoming.book.id,
            time: upcoming.event.time,
            title: upcoming.book.title,
            event: upcoming.event.event,
          }
        : null;
    } catch {
      nextEvent.value = null;
    }
  }

  const byId = (id: number) => books.value.find((b) => b.id === id) ?? null;

  /** Ro'yxatdagi bitta kitobni yangilash (favorite / status o'zgarganda) */
  function patch(id: number, changes: Partial<EmployeeBookItem>): void {
    books.value = books.value.map((b) => (b.id === id ? { ...b, ...changes } : b));
  }

  const stats = computed(() => ({
    total: books.value.length,
    passed: books.value.filter((b) => b.employee_status === 'exam_passed').length,
    scheduled: books.value.filter((b) => b.enrolled && b.employee_status !== 'exam_passed').length,
    favorites: books.value.filter((b) => b.favorite).length,
  }));

  function reset(): void {
    books.value = [];
    index.value = null;
    nextEvent.value = null;
    loaded.value = false;
  }

  return {
    books,
    index,
    nextEvent,
    loaded,
    loading,
    loadAll,
    invalidate,
    loadIndex,
    loadNextEvent,
    byId,
    patch,
    stats,
    reset,
  };
});
